import React from 'react'
import { Input, Sidebar } from "@iq/models"
import { LuSave, LuX } from "react-icons/lu"
import { createProduct, uploadProductImage } from "../../services/productService"
import "./createProductModalStyles.css"

let selectedImageCounter = 0;

export default function CreateProductModal({
  isOpen,
  onClose,
  onCreated = () => {},
  isEditing = false,
  initialProduct,
  onSaveChanges,
}) {
  const [productName, setProductName] = React.useState("");
  const [productDescription, setProductDescription] = React.useState("");
  const [basePrice, setBasePrice] = React.useState("");
  const [baseDuration, setBaseDuration] = React.useState("");
  const [selectedImages, setSelectedImages] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const fileInputRef = React.useRef(null);
  const selectedImagesRef = React.useRef([]);
  const initialProductRef = React.useRef(initialProduct);
  const busyRef = React.useRef(false);
  const hostRef = React.useRef(null);

  selectedImagesRef.current = selectedImages;
  initialProductRef.current = initialProduct;

  React.useEffect(() => () => {
    selectedImagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    selectedImagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    const product = isEditing ? initialProductRef.current : null;
    setProductName(product?.name || "");
    setProductDescription(product?.description || "");
    setBasePrice(product ? (Number(product.base_price ?? product.BasePrice ?? 0) / 100).toFixed(2) : "");
    setBaseDuration(product ? String(product.base_duration ?? product.BaseDuration ?? 0) : "");
    setSelectedImages([]);
    setSaving(false);
    setErrorMessage("");
  }, [isOpen, isEditing]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    const frame = requestAnimationFrame(() => hostRef.current?.querySelector("input")?.focus());
    return () => {
      cancelAnimationFrame(frame);
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  const handleSelectImages = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      return;
    }

    setSelectedImages((currentImages) => [
      ...currentImages,
      ...files.map((file) => {
        selectedImageCounter += 1;
        return {
          id: `image-${selectedImageCounter}`,
          file,
          previewUrl: URL.createObjectURL(file),
        };
      }),
    ]);

    event.target.value = "";
  };

  const handleRemoveImage = (imageId) => {
    setSelectedImages((currentImages) => currentImages.filter((image) => {
      if (image.id !== imageId) {
        return true;
      }

      URL.revokeObjectURL(image.previewUrl);
      return false;
    }));
  };

  const handleSave = async () => {
    if (busyRef.current) return;
    const trimmedName = productName.trim();
    const trimmedDescription = productDescription.trim();
    const price = basePrice.trim() || (isEditing ? "" : "0");
    const duration = baseDuration.trim() || (isEditing ? "" : "0");
    const cents = Math.round(Number(price) * 100);

    if (!trimmedName || (!isEditing && !trimmedDescription)) {
      setErrorMessage(isEditing ? "Enter a service name." : "Service name and description are required.");
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(price) || !Number.isSafeInteger(cents)) {
      setErrorMessage("Enter a non-negative base price with no more than two decimal places.");
      return;
    }
    if (!/^\d+$/.test(duration) || !Number.isSafeInteger(Number(duration))) {
      setErrorMessage("Enter a non-negative whole number of minutes.");
      return;
    }

    busyRef.current = true;
    setSaving(true);
    setErrorMessage("");

    try {
      const details = {
        name: trimmedName,
        description: trimmedDescription,
        base_price: cents,
        base_duration: Number(duration),
      };
      if (isEditing) {
        if (!onSaveChanges) throw new Error("Saving service changes is not connected.");
        await onSaveChanges(details);
        onClose();
        return;
      }

      const payload = await createProduct(details);

      const createdProduct = payload?.product ?? payload ?? null;
      const createdProductId = Number(createdProduct?.id ?? createdProduct?.ID ?? 0);
      const failedImageUploads = [];

      if (createdProductId > 0) {
        for (const image of selectedImages) {
          try {
            await uploadProductImage(createdProductId, image.file, { altText: trimmedName });
          } catch (uploadError) {
            failedImageUploads.push(`${image.file.name}: ${uploadError?.message || "upload failed"}`);
          }
        }
      }

      await onCreated(createdProduct, { failedImageUploads });
    } catch (error) {
      setErrorMessage(error?.message || (isEditing ? "Failed to save service details." : "Failed to create service."));
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  const requestClose = () => { if (!busyRef.current) onClose(); };
  const handleKeys = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      requestClose();
    }
    if (event.key !== "Tab") return;
    const controls = Array.from(hostRef.current.querySelectorAll("button:not(:disabled), input:not(:disabled), textarea:not(:disabled), [tabindex='0']"))
      .filter((element) => element.getClientRects().length > 0);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (!controls.includes(document.activeElement) || (!event.shiftKey && document.activeElement === last)) {
      event.preventDefault();
      first?.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    }
  };
  const isSaveDisabled = saving || (!isEditing && (!productName.trim() || !productDescription.trim()));

  const actionButtons = [
    {
      key: "cancel",
      text: "Cancel",
      onClick: requestClose,
      disabled: saving,
      style: { background: "var(--surface-1)", color: "var(--fg-default)", borderColor: "var(--border-default)" },
    },
    {
      key: "save",
      text: saving ? "Saving..." : isEditing ? "Save changes" : "Save Service",
      icon: <LuSave size={16} />,
      onClick: handleSave,
      disabled: isSaveDisabled,
    },
  ];

  return (
    <div ref={hostRef} className="createProductSidebarHost" onKeyDown={handleKeys}>
    <Sidebar
        isOpen={isOpen}
        onClose={requestClose}
        title={isEditing ? "Edit Service" : "Create Service"}
        description="Set the service name, description, base price, and duration."
        actionButtons={actionButtons}
        styles={{ panel: { width: "min(100%, 36rem)" } }}
    >
        <div className="createProductModalContainer">
            <Input 
              label="Service name"
              type="text"
              placeholder="Ex: 'Gel Manicure'"
              value={productName}
              onChange={(event) => setProductName(event.target.value)}
              disabled={saving}
            />
            <Input
              label="Description"
              type="textarea"
              placeholder="Ex: 'A long lasting gel manicure.'"
              value={productDescription}
              onChange={(event) => setProductDescription(event.target.value)}
              disabled={saving}
            />

            <div className="createProductModalAttributesContainer">
                <Input 
                  label="Base price ($)"
                  type="text"
                  inputMode="decimal"
                  placeholder="Ex: '29.99'"
                  value={basePrice}
                  onChange={(event) => setBasePrice(event.target.value)}
                  disabled={saving}
                />
                <Input 
                  label="Base duration (minutes)"
                  type="text"
                  inputMode="numeric"
                  placeholder="Ex: '30'"
                  value={baseDuration}
                  onChange={(event) => setBaseDuration(event.target.value)}
                  disabled={saving}
                />

            </div>
            {!isEditing && <div className="createProductModalImageContainer">
              <div className="createProductModalImagePreview">
                {selectedImages.length > 0 ? (
                  selectedImages.map((image) => (
                    <div key={image.id} className="createProductModalImagePreviewCard">
                      <img src={image.previewUrl} alt={image.file.name} />
                      <button
                        type="button"
                        className="createProductModalImageRemoveButton"
                        onClick={() => handleRemoveImage(image.id)}
                        disabled={saving}
                        aria-label={`Remove ${image.file.name}`}
                      >
                        <LuX size={12} />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="createProductModalImageEmptyState">No images selected.</p>
                )}
              </div>
              <div className="createProductModalImageUpload">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleSelectImages}
                  className="createProductModalImageInput"
                />
                <button
                  type="button"
                  className="createProductModalImageUploadButton"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={saving}
                >
                  Upload Images
                </button>
              </div>
            </div>}
            {errorMessage && <p className="createProductError" role="alert">{errorMessage}</p>}
        </div>
    </Sidebar>
    </div>
  )
}
